import { useOutsideClick } from '@quadminds/hooks';
import { useVirtualizer } from '@tanstack/react-virtual';
import clsx from 'clsx';
import { ChangeEvent, Fragment, KeyboardEvent, useRef, useState, useMemo, useLayoutEffect, LegacyRef } from 'react';
import { twMerge } from 'tailwind-merge';
import { ComboboxOption, ComboboxProps, MultipleComboboxProps, SingleComboboxProps } from './combobox.types';
import { Chip } from '../chip/chip';
import Icon from '../icon/icon';
import { InputAutocompleteOption } from '../input-autocomplete-option/input-autocomplete-option';
import { InputAutocompleteSimpleLayout } from '../input-autocomplete-simple-layout/input-autocomplete-simple-layout.component';
import {
    ACCORDION_PADDING,
    MIN_OPTION_HEIGHT,
    MIN_OPTION_VISIBLE,
} from '../input-autocomplete/input-autocomplete.constants';
import { getInputContainerClasses } from '../input/input';

function filterOptions(options: ComboboxOption[], query: string): ComboboxOption[] {
    if (!query) return options;
    const lower = query.toLocaleLowerCase();
    return options.filter((o) => o.label.toLocaleLowerCase().includes(lower));
}

function isSingle(props: ComboboxProps): props is SingleComboboxProps {
    return !props.multiple;
}

function isMultiple(props: ComboboxProps): props is MultipleComboboxProps {
    return props.multiple === true;
}

export function Combobox(props: ComboboxProps) {
    const {
        name,
        label,
        placeholder,
        options,
        loading = false,
        disabled = false,
        required = false,
        error = false,
        errorMessage,
        query: controlledQuery,
        onSearch,
        onScrollEnd,
        onBlur,
        onClick,
        onFocus,
        renderOption,
        onUnknownOption,
        minVisible = MIN_OPTION_VISIBLE,
        pillCount = 3,
        containerClasses,
        inputClassName,
        optionDropdownClasses,
        position = 'bottom',
    } = props;

    const modalRef = useRef<HTMLDivElement | null>(null);
    const inputRef = useRef<HTMLInputElement | null>(null);
    // Prevent re-entrancy on scroll-end
    const scrollEndFiredRef = useRef(false);

    const [open, setOpen] = useState(false);
    const openRef = useRef(open);
    openRef.current = open;

    const containerRef = useOutsideClick(() => handleToggleOpen(false)) as LegacyRef<HTMLDivElement>;
    // null → mirror selection display; string → user is typing
    const [internalQuery, setInternalQuery] = useState<string | null>(null);

    const isControlledSearch = controlledQuery !== undefined;
    const activeQuery = isControlledSearch ? controlledQuery : internalQuery ?? '';

    // What the text input shows
    const displayText =
        internalQuery !== null
            ? internalQuery
            : isControlledSearch
            ? controlledQuery
            : isSingle(props)
            ? props.selectedOption?.label ?? ''
            : '';

    const dropdownOptions = useMemo(() => {
        let base = options;
        if (!onSearch && activeQuery) {
            base = filterOptions(base, activeQuery);
        }
        return base;
    }, [options, activeQuery, onSearch]);

    const rowVirtualizer = useVirtualizer({
        count: dropdownOptions.length,
        getScrollElement: () => modalRef.current,
        estimateSize: () => MIN_OPTION_HEIGHT,
    });

    const virtualItems = rowVirtualizer.getVirtualItems();

    // Scroll-end detection — runs after commit, never during render
    const lastItem = virtualItems[virtualItems.length - 1];
    useLayoutEffect(() => {
        if (loading) {
            scrollEndFiredRef.current = false;
            return;
        }
        if (lastItem && lastItem.index === dropdownOptions.length - 1 && !scrollEndFiredRef.current && onScrollEnd) {
            scrollEndFiredRef.current = true;
            onScrollEnd();
        }
    }, [lastItem, dropdownOptions.length, loading, onScrollEnd]);

    function handleInputChange(e: ChangeEvent<HTMLInputElement>) {
        const text = e.target.value;
        setInternalQuery(text);
        onSearch?.(text);
        if (!open) setOpen(true);
    }

    function handleKeyDown(e: KeyboardEvent<HTMLInputElement>) {
        if (e.key !== 'Enter') return;
        const query = internalQuery ?? '';
        if (!query) return;
        const exact = dropdownOptions.find((o) => o.label.toLowerCase() === query.toLowerCase());
        if (exact) {
            handleSelect(exact);
        } else if (onUnknownOption) {
            e.preventDefault();
            onUnknownOption(query);
            setInternalQuery(null);
        }
    }

    function handleSelect(item: ComboboxOption) {
        if (isMultiple(props)) {
            const alreadySelected = props.value.includes(item.value);
            const next = alreadySelected ? props.value.filter((v) => v !== item.value) : [...props.value, item.value];
            const nextOpts = alreadySelected
                ? props.selectedOptions.filter((o) => o.value !== item.value)
                : [...props.selectedOptions, item];
            props.onChange(next, nextOpts);
            setInternalQuery(null);
        } else {
            props.onChange(item.value, item);
            setInternalQuery(null);
            setOpen(false);
        }
    }

    function handleDeselect(value: string) {
        if (!isMultiple(props)) return;
        props.onChange(
            props.value.filter((v) => v !== value),
            props.selectedOptions.filter((o) => o.value !== value),
        );
    }

    function handleToggleOpen(val?: boolean) {
        const next = val !== undefined ? val : !openRef.current;
        if (!next) {
            setOpen(false);
            setInternalQuery(null);
        } else {
            setOpen(true);
        }
    }

    const optionsContainerClasses = twMerge(
        position === 'top'
            ? 'overflow-auto w-full shadow-lg border border-gray-50 rounded-t-lg bg-white absolute z-[50] bottom-full mb-1'
            : 'overflow-auto w-full shadow-lg border border-gray-50 rounded-b-lg bg-white absolute z-[50] top-full mt-1',
        open && dropdownOptions.length > 0 && 'py-2',
        optionDropdownClasses,
    );

    const optionItemClasses = 'flex justify-start items-center pl-2 font-light hover:bg-gray-50';

    const dynamicMinOptions = Math.min(dropdownOptions.length || 1, minVisible);
    const dynamicHeight =
        open && dropdownOptions.length > 0 ? MIN_OPTION_HEIGHT * dynamicMinOptions + ACCORDION_PADDING * 2 : 0;

    const optionItems = virtualItems.map((virtualRow) => {
        const item = dropdownOptions[virtualRow.index];
        const style = {
            position: 'absolute' as const,
            top: 0,
            left: 0,
            width: '100%',
            minHeight: `${MIN_OPTION_HEIGHT}px`,
            transform: `translateY(${virtualRow.start}px)`,
        };

        if (renderOption) {
            const isSelected = isSingle(props) ? props.value === item.value : props.value.includes(item.value);
            return (
                <div
                    key={item.value}
                    ref={rowVirtualizer.measureElement}
                    data-index={virtualRow.index}
                    data-test-id={`${name}-combo-option`}
                    style={style}
                    role="button"
                    tabIndex={item.disabled ? -1 : 0}
                    onClick={() => !item.disabled && handleSelect(item)}
                    onKeyDown={(evt) => {
                        if (!item.disabled && (evt.key === 'Enter' || evt.key === ' ')) {
                            evt.preventDefault();
                            handleSelect(item);
                        }
                    }}
                    className={clsx(optionItemClasses, item.disabled && 'opacity-50 cursor-not-allowed')}
                >
                    {renderOption(item, style, isSelected)}
                </div>
            );
        }

        const isSelected = isSingle(props) ? props.value === item.value : props.value.includes(item.value);
        return (
            <Fragment key={item.value}>
                <InputAutocompleteOption
                    ref={rowVirtualizer.measureElement}
                    dataIndex={virtualRow.index}
                    dataTestId={`${name}-combo-option`}
                    value={item.value}
                    label={item.label}
                    style={style}
                    className={optionItemClasses}
                    action={() => handleSelect(item)}
                    multiple={isMultiple(props)}
                    selected={isSelected}
                    disabled={item.disabled}
                />
            </Fragment>
        );
    });

    const dropdownEl = (
        <div
            ref={modalRef}
            className={optionsContainerClasses}
            style={
                dynamicHeight === 0
                    ? { height: 0, padding: 0, border: 'none' }
                    : { height: dynamicHeight, display: 'block' }
            }
            data-testid={`${name}-autocomplete-container`}
        >
            <div
                className="leading-none"
                style={{ height: `${rowVirtualizer.getTotalSize()}px`, width: '100%', position: 'relative' }}
            >
                {optionItems}
            </div>
        </div>
    );

    if (isMultiple(props)) {
        const selectedOptions = props.selectedOptions;
        return (
            // eslint-disable-next-line jsx-a11y/no-static-element-interactions
            <div
                className={clsx('relative', containerClasses)}
                data-test-id={props['data-test-id']}
                onKeyDown={handleKeyDown}
            >
                {label && (
                    <label className="block mb-1 text-sm font-medium text-black" htmlFor={name}>
                        {label}
                        {required && <span className="text-red-600"> *</span>}
                    </label>
                )}
                <div ref={containerRef}>
                    {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions */}
                    <div
                        className={twMerge(
                            getInputContainerClasses({ disabled, error }),
                            'relative flex gap-1 items-center overflow-hidden cursor-text',
                            inputClassName,
                        )}
                        onClick={() => {
                            if (!disabled) {
                                handleToggleOpen();
                                inputRef.current?.focus();
                            }
                        }}
                    >
                        {selectedOptions.length <= pillCount ? (
                            <div className="absolute left-0 flex overflow-hidden" style={{ width: 'calc(100% - 10%)' }}>
                                {selectedOptions.map((opt) => (
                                    <Chip
                                        key={opt.value}
                                        className="m-0 shrink-0"
                                        text={opt.label}
                                        size="xxs"
                                        onClose={() => handleDeselect(opt.value)}
                                        disabled={disabled}
                                    />
                                ))}
                            </div>
                        ) : (
                            <Chip
                                className="absolute l-0 m-0 shrink-0 grow-0"
                                text={`${selectedOptions.length} seleccionados`}
                                size="xxs"
                                onClose={() => props.onChange([], [])}
                                disabled={disabled}
                            />
                        )}
                        <input
                            ref={inputRef}
                            data-test-id={`${name}-combo-input`}
                            className="outline-none shrink min-w-[60px] flex-1 bg-transparent text-sm"
                            value={displayText}
                            onChange={handleInputChange}
                            placeholder={selectedOptions.length === 0 ? placeholder : ''}
                            disabled={disabled}
                            onFocus={onFocus}
                            onBlur={onBlur}
                            onClick={onClick}
                        />
                        {loading ? (
                            <div className="transition-all text-primary animate-spin shrink-0">
                                <Icon name="ri-loader-4-line" className="text-gray-100" size={20} />
                            </div>
                        ) : (
                            <div className={twMerge('transition shrink-0 grow-0 h-5', open && 'rotate-180')}>
                                <Icon name="ri-arrow-down-s-line" className="text-gray-100" size={20} />
                            </div>
                        )}
                    </div>
                    {dropdownEl}
                </div>
            </div>
        );
    }

    return (
        // eslint-disable-next-line jsx-a11y/no-static-element-interactions
        <div
            className={clsx('relative', containerClasses)}
            data-test-id={props['data-test-id']}
            onKeyDown={handleKeyDown}
            ref={containerRef}
        >
            <InputAutocompleteSimpleLayout
                ref={modalRef}
                name={name}
                label={label}
                show={open}
                query={displayText}
                testId={props['data-test-id'] ?? `${name}-combobox`}
                isLoading={loading}
                minVisible={minVisible}
                optionsLength={dropdownOptions.length}
                optionsHeight={rowVirtualizer.getTotalSize()}
                handleShow={handleToggleOpen}
                handleInputChange={handleInputChange}
                inputClassName={inputClassName}
                optionsContainerClasses={optionsContainerClasses}
                error={error}
                errorMessage={error ? errorMessage : ''}
                disabled={disabled}
                required={required}
                placeholder={placeholder}
                onClick={onClick}
                onFocus={onFocus}
                onBlur={onBlur}
            >
                {optionItems}
            </InputAutocompleteSimpleLayout>
        </div>
    );
}

export default Combobox;
